import {
  PinwheelExperienceInput,
  PlaidEntryPointInput,
  PlaidProduct,
  SubTitleGroup,
} from '@varo/graphql-schema-types';
import {
  NativeStackHeaderProps,
  Stack,
  useLocalSearchParams,
  usePathname,
  useRouter,
} from 'expo-router';
import {
  getNavigationDecision,
  NavigationDecisionType,
} from 'features/advance/helpers/lightning/getNavigationDecision';
import handleEligibilityNavigation, {
  AdvanceFlow,
} from 'features/advance/helpers/lightning/handleEligibilityNavigation';
import { getPlaidLoadingMessage } from 'features/advance/helpers/lightning/Lightning.helpers';
import useFocusedConsumerReportsAccount from 'features/advance/hooks/lightning/craUpgrade/useFocusedConsumerReportsAccount';
import useGetEligibilityScreenContent from 'features/advance/hooks/lightning/eligibility/useGetEligibilityScreenContent';
import useGetLightningEligibility from 'features/advance/hooks/lightning/useGetLightningEligibility';
import DirectDepositLoader from 'features/directDeposit/screens/DirectDepositLoader';
import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ScrollView, StyleSheet, View } from 'react-native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import Button from 'shared/components/vxl/Button/Button';
import ContentList from 'shared/components/vxl/ContentList/ContentList';
import Icon from 'shared/components/vxl/icon/Icon/Icon';
import FullScreenLoading from 'shared/components/vxl/loading/FullScreenLoading/FullScreenLoading';
import ExpoNavigationBar from 'shared/components/vxl/navigation/ExpoNavigationBar/ExpoNavigationBar';
import BodyBaseBoldText from 'shared/components/vxl/typography/primary/body/BodyBaseBoldText';
import BodyBaseText from 'shared/components/vxl/typography/primary/body/BodyBaseText';
import BodyXSText from 'shared/components/vxl/typography/primary/body/BodyXSText';
import TitleXLText from 'shared/components/vxl/typography/primary/title/TitleXLText';
import { isAndroid } from 'shared/constants/platform';
import { useViewDidFocusScreenAnalyticsEvent } from 'shared/hooks/analytics/useViewDidFocusAnalytics';
import useDefaultError from 'shared/hooks/errorFormatters/useDefaultError/useDefaultError';
import { useDirectDepositLoadingComponent } from 'shared/hooks/pinwheel/useDirectDepositLoadingComponent/useDirectDepositLoadingComponent';
import useDirectDepositManagementFlow from 'shared/hooks/pinwheel/useDirectDepositManagementFlow/useDirectDepositManagementFlow';
import usePinwheel from 'shared/hooks/pinwheel/usePinwheel/usePinwheel';
import { useBackHandler } from 'shared/hooks/useBackHandler/useBackHandler';
import useDismissToRootTab, {
  Tabs,
} from 'shared/hooks/useDismissToRootTab/useDismissToRootTab';
import { PlaidIntent, PlaidIntentTypes } from 'shared/hooks/usePlaidV2/types';
import usePlaidV2 from 'shared/hooks/usePlaidV2/usePlaidV2';
import ErrorScreen, {
  ErrorScreenType,
} from 'shared/patterns/vxl/errors/ErrorScreen/ErrorScreen';
import ScreenTransitionOverlay from 'shared/patterns/vxl/ScreenTransitionOverlay/ScreenTransitionOverlay';
import theme from 'shared/styles/theme';
import useVaroAdvanceFlow from 'shared/utils/useVaroAdvanceFlow/useVaroAdvanceFlow';

const Eligibility = () => {
  const router = useRouter();
  const [plaidSuccess, setPlaidSuccess] = useState(false);
  const [plaidIntent, setPlaidIntent] = useState<PlaidIntentTypes>({
    intent: PlaidIntent.Wait,
  });

  const [showLoadingScreen, setShowLoadingScreen] = useState(false);
  const openPlaidInFlightRef = useRef(false);

  const { t: translatedText } = useTranslation('advance', {
    keyPrefix: 'lightningEligibility',
  });

  const { ineligibility = 'false' } = useLocalSearchParams<{
    ineligibility?: string;
  }>();

  const { bottom } = useSafeAreaInsets();
  const dismissToTab = useDismissToRootTab();

  const {
    data: advanceConsumerReportPermissionScreen,
    fetching: fetchingConsumerReportPermissionScreen,
    error: consumerReportPermissionScreenError,
    refetch: refetchConsumerReportPermissionScreen,
    isConsumerReportPaused,
  } = useFocusedConsumerReportsAccount({ requestPolicy: 'network-only' });

  const hasAccountsNeedingUpgrade =
    !!advanceConsumerReportPermissionScreen?.permissionRequired?.length;

  const pathName = usePathname();

  const {
    openPlaid,
    ready: plaidReady,
    fetching: plaidFetching,
    error: plaidHasError,
    reset: resetPlaid,
  } = usePlaidV2(plaidIntent, {
    sourceName: pathName,
  });

  useEffect(() => {
    if (!plaidReady || openPlaidInFlightRef.current) {
      return;
    }

    const openPlaidIfReady = async () => {
      openPlaidInFlightRef.current = true;
      setPlaidSuccess(false);

      try {
        const result = await openPlaid();
        switch (result.status) {
          case 'success':
            setPlaidSuccess(true);
            break;
          case 'exit':
          case 'error':
          default:
            setShowLoadingScreen(false);
        }
      } catch (_error) {
        setPlaidIntent({ intent: PlaidIntent.Wait });
        resetPlaid();
        setShowLoadingScreen(false);
      } finally {
        setPlaidIntent({ intent: PlaidIntent.Wait });
        openPlaidInFlightRef.current = false;
      }
    };

    void openPlaidIfReady();
  }, [plaidReady, openPlaid, resetPlaid]);

  const flow = useVaroAdvanceFlow();
  const { initiatePinwheel, isPinwheelLoading } = usePinwheel();

  const { skipIntroScreen } = useDirectDepositLoadingComponent();

  const {
    result: advanceScreenContent,
    errorModel: advanceScreenContentErrorModel,
    loading: advanceScreenContentLoading,
    refetch: refetchEligibilityScreenContent,
  } = useGetEligibilityScreenContent();

  const howToQualifyScreenContent =
    advanceScreenContent?.howToQualifyScreenContent;
  const subtitleGroups: SubTitleGroup[] =
    howToQualifyScreenContent?.subTitleGroups ?? [];

  const {
    shouldRedirectToDDManagementPage,
    shouldLaunchPinwheelSDK,
    isLoading: pinwheelFlowLoading,
    pinwheelFlowPageError,
    refetchDdManagementFlow,
    handleDdManagementLinkTokenFailure,
  } = useDirectDepositManagementFlow({});

  const defaultError = useDefaultError();

  const {
    fetching: fetchingEligibilityStatus,
    hasError: existingFlowHasError,
    counter,
    eligibilityStatus: status,
    data: eligibilityData,
    isFinalEligibilityStatus,
  } = useGetLightningEligibility({
    shouldPoll: plaidSuccess,
  });

  const hasError =
    existingFlowHasError ||
    !!advanceScreenContentErrorModel ||
    plaidHasError ||
    consumerReportPermissionScreenError;

  const goHome = () => {
    setPlaidIntent({ intent: PlaidIntent.Wait });
    dismissToTab(Tabs.HOME);
  };

  useBackHandler(goHome);

  const handleDirectDeposit = () => {
    if (shouldRedirectToDDManagementPage) {
      return router.push(
        '/move-money/direct-deposit/direct-deposit-management',
      );
    }

    const outcomeCallback = shouldLaunchPinwheelSDK
      ? refetchDdManagementFlow
      : goHome;

    initiatePinwheel({
      input: {
        experience: PinwheelExperienceInput.DirectDepositSwitch,
        skipIntroScreen,
      },
      launchBehavior: skipIntroScreen ? { delayAfterTokenMs: 3000 } : undefined,
      onOutcome: outcomeCallback,
      ...(shouldLaunchPinwheelSDK && {
        onLinkTokenError: handleDdManagementLinkTokenFailure,
      }),
    });
  };

  useViewDidFocusScreenAnalyticsEvent({
    key: '/varo-advance/link-account/va_eligibility',
    customAttributes: {
      source_name:
        ineligibility === 'true' ? 'va_ineligibility' : 'va_lost_eligibility',
      title: howToQualifyScreenContent?.title ?? '',
    },
  });

  const loadingMessage = getPlaidLoadingMessage({ counter, translatedText });

  const isLoading =
    isConsumerReportPaused ||
    fetchingEligibilityStatus ||
    showLoadingScreen ||
    isPinwheelLoading ||
    advanceScreenContentLoading ||
    plaidFetching ||
    fetchingConsumerReportPermissionScreen;

  const { advanceProductsQualified, advanceProductsNotQualified } =
    eligibilityData;

  useEffect(() => {
    const decision = getNavigationDecision({
      craUpgrade: {
        hasPermissionRequired: hasAccountsNeedingUpgrade,
        shouldAttemptCraUpgrade: plaidSuccess,
      },
      eligibility: {
        hasError: Boolean(hasError),
        isFinalEligibilityStatus,
        fetchingEligibilityStatus,
        status: status ?? null,
      },
    });

    if (decision === NavigationDecisionType.CRA_UPGRADE) {
      router.dismissTo(
        `/${flow}/link-account/cra-upgrade` as `/${AdvanceFlow}/link-account/cra-upgrade`,
      );
      return;
    }

    if (decision === NavigationDecisionType.ELIGIBILITY) {
      if (advanceProductsNotQualified && showLoadingScreen) {
        refetchEligibilityScreenContent();
        setShowLoadingScreen(false);
        refetchConsumerReportPermissionScreen();
        return;
      }

      if (advanceProductsNotQualified) {
        return;
      }

      handleEligibilityNavigation({
        status: status ?? null,
        advanceProductsQualified,
      });
    }
  }, [
    advanceProductsNotQualified,
    advanceProductsQualified,
    fetchingEligibilityStatus,
    flow,
    hasAccountsNeedingUpgrade,
    hasError,
    isFinalEligibilityStatus,
    plaidIntent,
    refetchConsumerReportPermissionScreen,
    refetchEligibilityScreenContent,
    showLoadingScreen,
    status,
    router,
    plaidSuccess,
  ]);

  const handleAddDifferentAccountPress = () => {
    if (hasAccountsNeedingUpgrade) {
      setPlaidIntent({ intent: PlaidIntent.Wait });
      setShowLoadingScreen(false);
      router.replace(
        `/${flow}/link-account/cra-upgrade` as `/${AdvanceFlow}/link-account/cra-upgrade`,
      );
      return;
    }

    setShowLoadingScreen(true);
    setPlaidIntent({
      intent: PlaidIntent.Link,
      product: PlaidProduct.Auth,
      entryPoint: PlaidEntryPointInput.Advance,
    });
  };

  return (
    <View style={styles.screenContainer}>
      <Stack.Screen
        options={{
          headerShown: true,
          header: (props: NativeStackHeaderProps) => (
            <ExpoNavigationBar
              {...props}
              primaryIcon={!isLoading ? <Icon iconKey="CLOSE" /> : null}
              primaryOnPress={goHome}
            />
          ),
        }}
      />
      <ScreenTransitionOverlay
        isLoading={(isLoading && !hasError) || pinwheelFlowLoading}
        LoadingComponent={
          <DirectDepositLoader
            isLoadingConditions={
              (fetchingEligibilityStatus ||
                showLoadingScreen ||
                advanceScreenContentLoading ||
                plaidFetching ||
                fetchingConsumerReportPermissionScreen) &&
              !hasError
            }
            isPinwheelLoading={pinwheelFlowLoading}
            defaultLoadingComponent={
              <FullScreenLoading
                isCoinLoader
                titleText={loadingMessage || ' '}
                bodyText={translatedText('loadingScreen.body')}
              />
            }
          />
        }
        ErrorComponent={
          hasError ||
          pinwheelFlowPageError ||
          !!advanceScreenContentErrorModel ? (
            <ErrorScreen
              screenType={ErrorScreenType.Error}
              messageHeadline={defaultError.title}
              messageBody={defaultError.message}
              primaryButton={{
                title: translatedText('errorButton'),
                onPress: goHome,
              }}
            />
          ) : null
        }
      />
      <>
        <ScrollView
          contentContainerStyle={[
            styles.scrollContentContainer,
            styles.defaultPadding,
          ]}
        >
          <TitleXLText numberOfLines={0}>
            {howToQualifyScreenContent?.title}
          </TitleXLText>
          <View>
            <View style={styles.bodyContainer} testID="eligibility-v2">
              <BodyBaseText numberOfLines={0}>
                {howToQualifyScreenContent?.description}
              </BodyBaseText>
              {subtitleGroups.map((group, i) => (
                <React.Fragment key={`${group.subTitle}`}>
                  <BodyBaseBoldText
                    numberOfLines={1}
                    style={i === 1 ? styles.topPadding : undefined}
                  >
                    {group.subTitle}
                  </BodyBaseBoldText>
                  <ContentList contentList={group.bullets} />
                </React.Fragment>
              ))}
              <BodyBaseText numberOfLines={0}>
                {howToQualifyScreenContent?.content}
              </BodyBaseText>
              {howToQualifyScreenContent?.termsAndConditionsCopy ? (
                <BodyXSText style={styles.termsApply} numberOfLines={0}>
                  {howToQualifyScreenContent.termsAndConditionsCopy}
                </BodyXSText>
              ) : null}
            </View>
          </View>
        </ScrollView>

        <View
          style={[
            styles.footerContainer,
            {
              paddingBottom: isAndroid
                ? bottom + theme.spacing.m
                : bottom + theme.spacing.l,
            },
          ]}
        >
          <Button
            label={translatedText(
              shouldRedirectToDDManagementPage
                ? 'buttons.manageDD'
                : 'buttons.setUpDD',
            )}
            onPress={handleDirectDeposit}
            isDisabled={false}
            isLoading={false}
            analyticsId="directDeposit"
            customAttributes={{
              source_name:
                ineligibility === 'true'
                  ? 'va_ineligibility'
                  : 'va_lost_eligibility',
            }}
            size="LARGE"
            type="PRIMARY"
            width="FULL"
          />
          <Button
            label={translatedText('buttons.addDifferentAccount')}
            onPress={handleAddDifferentAccountPress}
            isDisabled={false}
            isLoading={false}
            analyticsId="linkingAccount"
            customAttributes={{
              source_name:
                ineligibility === 'true'
                  ? 'va_ineligibility'
                  : 'va_lost_eligibility',
            }}
            size="LARGE"
            type="SECONDARY"
            width="FULL"
          />
        </View>
      </>
    </View>
  );
};

const styles = StyleSheet.create({
  bodyContainer: {
    paddingBottom: theme.spacing.m,
    rowGap: theme.spacing.m,
  },
  defaultPadding: {
    padding: theme.spacing.m,
  },
  footerContainer: {
    borderTopColor: theme.color.border.primarySolid,
    borderTopWidth: 1,
    gap: theme.spacing.m,
    padding: theme.spacing.m,
  },
  screenContainer: {
    backgroundColor: theme.color.background.primary,
    flex: 1,
  },
  scrollContentContainer: {
    flexGrow: 1,
    gap: theme.spacing.m,
  },
  termsApply: {
    color: theme.color.text.secondary,
    paddingTop: theme.spacing.m,
  },
  topPadding: {
    paddingTop: theme.spacing.m,
  },
});

export default Eligibility;
